// NotificationBell, header icon with unread badge + dropdown
// Polls every 60s for new notifications (user_notifications + discovery_notifications).

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

interface Notification {
  id: string;
  title: string;
  body: string;
  link: string | null;
  read: boolean;
  created_at: string;
  _source?: 'user' | 'discovery' | 'notifications';
}

export function NotificationBell() {
  const { profile }               = useAuth();
  const [items,  setItems]        = useState<Notification[]>([]);
  const [open,   setOpen]         = useState(false);
  // Stop polling if the table is missing (404), avoids 60-second console spam
  // until the repair migration is applied.
  const [available, setAvailable] = useState(true);
  const dropRef                   = useRef<HTMLDivElement>(null);

  const unread = items.filter(n => !n.read).length;

  useEffect(() => {
    if (!profile?.id) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60_000);
    return () => clearInterval(interval);
  }, [profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  async function fetchNotifications() {
    if (!profile?.id || !available) return;
    const { data, error, status } = await supabase
      .from('user_notifications')
      .select('id, title, body, link, read, created_at')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false })
      .limit(10);
    // 404 → table doesn't exist yet (repair migration pending).
    // Disable polling until the next page load to avoid console spam.
    if (status === 404 || (error && status >= 400 && status < 500)) {
      setAvailable(false);
      return;
    }
    const userNotifs = ((data ?? []) as Notification[]).map(n => ({ ...n, _source: 'user' as const }));

    // Also load discovery notifications
    const { data: discData } = await supabase
      .from('discovery_notifications')
      .select('id, title, body, action_url, read, created_at')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false })
      .limit(5);

    const discNotifs: Notification[] = ((discData ?? []) as { id: string; title: string; body: string; action_url: string | null; read: boolean; created_at: string }[]).map(n => ({
      id:         n.id,
      title:      n.title,
      body:       n.body,
      link:       n.action_url,
      read:       n.read,
      created_at: n.created_at,
      _source:    'discovery' as const,
    }));

    // Also load typed notifications
    const { data: typedData } = await supabase
      .from('notifications')
      .select('id, title, body, action_url, read_at, created_at')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false })
      .limit(5);

    const typedNotifs: Notification[] = ((typedData ?? []) as { id: string; title: string; body: string; action_url: string | null; read_at: string | null; created_at: string }[]).map(n => ({
      id:         n.id,
      title:      n.title,
      body:       n.body,
      link:       n.action_url,
      read:       n.read_at !== null,
      created_at: n.created_at,
      _source:    'notifications' as const,
    }));

    const merged = [...userNotifs, ...discNotifs, ...typedNotifs].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    ).slice(0, 15);

    setItems(merged);
  }

  async function markAllRead() {
    if (!profile?.id || unread === 0) return;
    const userIds    = items.filter(n => !n.read && n._source === 'user').map(n => n.id);
    const discIds    = items.filter(n => !n.read && n._source === 'discovery').map(n => n.id);
    const typedIds   = items.filter(n => !n.read && n._source === 'notifications').map(n => n.id);
    if (userIds.length)  await supabase.from('user_notifications').update({ read: true }).in('id', userIds);
    if (discIds.length)  await supabase.from('discovery_notifications').update({ read: true }).in('id', discIds);
    if (typedIds.length) await supabase.from('notifications').update({ read_at: new Date().toISOString() }).in('id', typedIds);
    setItems(prev => prev.map(n => ({ ...n, read: true })));
  }

  async function markRead(id: string) {
    const item = items.find(n => n.id === id);
    if (!item) return;
    if (item._source === 'notifications') {
      await supabase.from('notifications').update({ read_at: new Date().toISOString() }).eq('id', id);
    } else {
      const table = item._source === 'discovery' ? 'discovery_notifications' : 'user_notifications';
      await supabase.from(table).update({ read: true }).eq('id', id);
    }
    setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  }

  return (
    <div ref={dropRef} style={{ position: 'relative' }}>
      <button
        className="btn btn-ghost btn-icon"
        aria-label={`Notificações${unread > 0 ? `, ${unread} não lidas` : ''}`}
        onClick={() => { setOpen(v => !v); if (!open) markAllRead(); }}
        style={{ position: 'relative' }}
      >
        <Bell size={20} />
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: '4px', right: '4px',
            minWidth: '16px', height: '16px', padding: '0 3px',
            backgroundColor: '#F5C842', color: '#0C0B0F',
            fontSize: '10px', fontWeight: 800, borderRadius: '999px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            lineHeight: 1, pointerEvents: 'none',
          }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="animate-fade-in" style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          width: '320px', maxHeight: '400px', overflowY: 'auto',
          backgroundColor: 'var(--bg-elevated)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-card)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          zIndex: 200,
        }}>
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ fontWeight: 700, color: 'var(--text-primary)', margin: 0, fontSize: 'var(--text-label)' }}>Notificações</p>
            <Link to="/notificacoes" style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', textDecoration: 'none' }}
              onClick={() => setOpen(false)}>
              Ver todas
            </Link>
          </div>

          {items.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', fontStyle: 'italic' }}>Nenhuma notificação</p>
            </div>
          )}

          {items.map(n => {
            const content = (
              <div key={n.id}
                onClick={() => markRead(n.id)}
                style={{
                  padding: '0.75rem 1rem',
                  borderBottom: '1px solid var(--border-subtle)',
                  backgroundColor: n.read ? 'transparent' : 'rgba(31,78,95,0.04)',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  {!n.read && (
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#F5C842', marginTop: '5px', flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.15rem', fontSize: 'var(--text-label)', lineHeight: 1.3 }}>{n.title}</p>
                    <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', margin: '0 0 0.15rem', lineHeight: 1.4 }}>{n.body}</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: '11px', margin: 0 }}>
                      {new Date(n.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                    </p>
                  </div>
                </div>
              </div>
            );
            return n.link ? (
              <Link key={n.id} to={n.link} style={{ textDecoration: 'none' }} onClick={() => setOpen(false)}>
                {content}
              </Link>
            ) : content;
          })}
        </div>
      )}
    </div>
  );
}
